import React, { useState } from "react";
import { Modal, Spinner, Alert } from "react-bootstrap";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import ItemService from "../../../../../services/customer_appCommonService";

const VendorBulkUploadModalMR = ({ show, onHide }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [excelData, setExcelData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  //  Read Excel for preview only
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const isExcel =
      selectedFile.name.endsWith(".xlsx") || selectedFile.name.endsWith(".xls");
    if (!isExcel) {
      setError("Please select a valid Excel file (.xlsx or .xls)");
      setFile(null);
      setExcelData([]);
      return;
    }

    setFile(selectedFile);
    setError("");

    // Read for preview
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        setExcelData(jsonData);
        setShowPreview(true);
      } catch (err) {
        setError("Failed to read Excel file");
        console.error(err);
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  //  Upload Excel file + metadata only
  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file");
      return;
    }

    if (excelData.length === 0) {
      setError("Excel file is empty");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      
      //  Append Excel file
      formData.append("file", file);
      
      //  Append metadata (NOT individual vendor data)
      formData.append("timeZone", "Asia/Calcutta");


      const res = await ItemService.vendorBulkUpload(formData);

      if (res?.status === 200) {
        toast.success(res?.message || "Bulk upload successful");
        resetModal();
        onHide();
        // setTimeout(() => window.location.reload(), 800);
      } else {
        throw new Error(res?.message || "Upload failed");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Upload failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const resetModal = () => {
    setFile(null);
    setExcelData([]);
    setShowPreview(false);
    setError("");
  };

  //  Dynamic columns for preview
  const columns = excelData.length > 0
    ? Object.keys(excelData[0]).map((key) => ({
        name: key.replace(/([A-Z])/g, ' $1').trim(),
        selector: (row) => row[key] || "--",
        sortable: true,
        wrap: true,
        width: "150px",
      }))
    : [];

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Vendor Bulk Upload</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <label className="form-label fw-bold">Select Excel File</label>
        <input
          type="file"
          className="form-control"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          disabled={uploading}
        />
        
        {file && (
          <div className="mt-2 p-2 bg-light rounded">
            <small className="text-success">
              <i className="bi bi-file-earmark-excel me-1"></i>
              <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
            </small>
          </div>
        )}

        {/*  Preview Table */}
        {showPreview && excelData.length > 0 && (
          <div className="mt-4">
            <h6 className="fw-bold">
              Preview
            </h6>
            <DataTable
              columns={columns}
              data={excelData}
              pagination
              paginationRowsPerPageOptions={[5, 10, 20, 50]}
              paginationPerPage={10}
              highlightOnHover
              striped
              dense
              fixedHeader
              fixedHeaderScrollHeight="400px"
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button
          className="btn btn-secondary"
          onClick={() => {
            resetModal();
            onHide();
          }}
          disabled={uploading}
        >
          <i className="bi bi-x-circle me-1"></i>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleUpload}
          disabled={uploading || !file || excelData.length === 0}
        >
          {uploading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Uploading vendors...
            </>
          ) : (
            <>
              <i className="bi bi-upload me-1"></i>
              Upload
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default VendorBulkUploadModalMR;
